import { Component, Element, forceUpdate, Host, Prop, h, Watch } from '@stencil/core';
import { uuidv4 } from '@/globals/helpers';
import { alert_exclamation_circle, alert_info_circle } from 'pn-design-assets/pn-assets/icons.js';

/**
 * The fielset is a wrapper to give context and information about inputs. Most commonly, a set of radio/checkboxes.
 * However, you may include any type of input such as textareas, toggles and selects.
 */
@Component({
  tag: 'pn-fieldset',
  styleUrl: 'pn-fieldset.scss',
})
export class PnFieldset {
  mo: MutationObserver;

  id: string = `pn-fieldset-${uuidv4()}`;
  idHelpertext: string = `${this.id}-helpertext`;

  @Element() hostElement: HTMLElement;

  /**
   * The legend provides a caption for the fieldset group.
   * This should be a question or heading that describes the inputs/fields.
   */
  @Prop() legend!: string;
  /** Text message underneath the legend for more instruction or hints on how to fill in controls correctly. */
  @Prop() helpertext?: string;
  /** Give the fieldset a custom HTML id. */
  @Prop() fieldsetId: string = this.id;
  /**
   * The id of the form you want the fieldset to be part of.
   * If you want the inputs inside to be associated with a specific form, you need to set on those elements directly.
   */
  @Prop() form?: string;
  /** Give the fieldset a HTML name. */
  @Prop() name?: string;
  /** Disable all of the inputs inside of the fieldset. */
  @Prop() disabled: boolean = false;
  /** Set the fieldset as `invalid`, will add red error styles. */
  @Prop() invalid: boolean = false;
  /** Error message, applies warning styles and icon. */
  @Prop() error?: string;

  @Watch('fieldsetId')
  handleId() {
    this.idHelpertext = `${this.fieldsetId}-helpertext`;
  }

  componentWillLoad() {
    this.handleId();
  }

  componentDidLoad() {
    if (this.mo) this.mo.disconnect();

    this.mo = new MutationObserver(() => forceUpdate(this.hostElement));

    this.mo.observe(this.hostElement.querySelector('.pn-fieldset'), { childList: true, subtree: true });
  }

  componentDidRender() {
    /**
     * Stencil has a known problem about rendering the `form` attribute.
     * Setting it via setAttribute solves it for now.
     * https://github.com/ionic-team/stencil/issues/2703
     **/
    if (this.form) {
      this.hostElement.querySelector('fieldset')?.setAttribute('form', this.form);
    }
  }

  hasError(): boolean {
    return this.invalid || !!this.error;
  }

  getSymbol(): string {
    if (this.disabled) return alert_info_circle;
    return alert_exclamation_circle;
  }

  render() {
    return (
      <Host>
        <fieldset
          id={this.fieldsetId}
          class="pn-fieldset"
          disabled={this.disabled}
          aria-describedby={this.helpertext && this.idHelpertext}
          aria-invalid={this.hasError().toString()}
        >
          <legend class="pn-fieldset-legend">{this.legend}</legend>
          {this.helpertext && (
            <p id={this.idHelpertext} class="pn-fieldset-helpertext">
              {this.helpertext}
            </p>
          )}

          <div class="pn-fieldset-items">
            <slot></slot>
          </div>

          <div class="pn-fieldset-message-wrapper" role="alert" hidden={!this.error}>
            <pn-icon icon={this.getSymbol()} />
            <p class="pn-fieldset-message">{this.error}</p>
          </div>
        </fieldset>
      </Host>
    );
  }
}
